import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import SEO from "../components/SEO";
import { getLandingAttribution, getUtmParams, getVisitorId, trackEvent, trackLandingEvent } from "../lib/analytics";
import { BUSINESS_TYPES, BUSINESS_TYPE_LABELS } from "@shared/businessTypes";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<boolean>(false);
  const [countdown, setCountdown] = useState(5);
  const [form, setForm] = useState({
    email: "", password: "", confirmPassword: "",
    firstName: "", lastName: "", companyName: "", phone: "",
    npiNumber: "", deaNumber: "", stateLicense: "",
    deaExpiration: "", stateLicenseExpiration: "",
    businessType: (() => {
      const type = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("type");
      return BUSINESS_TYPES.find(value => value === type) || "";
    })(),
  });
  type Field = keyof typeof form;
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({});

  const getFieldError = (field: Field) => {
    if (fieldErrors[field]) return fieldErrors[field];
    if (field === "npiNumber" && form.npiNumber.length > 0 && !/^\d{10}$/.test(form.npiNumber)) {
      return form.npiNumber.length < 10 ? "NPI must be exactly 10 digits." : "NPI must contain only digits.";
    }
  };
  const errorProps = (field: Field) => ({
    "aria-invalid": getFieldError(field) ? true as const : undefined,
    "aria-describedby": getFieldError(field) ? `reg-${field}-error` : undefined,
  });
  const fieldError = (field: Field) => getFieldError(field) && (
    <p id={`reg-${field}-error`} className="text-red-700 text-sm mt-1">{getFieldError(field)}</p>
  );
  const showValidationErrors = (errors: Partial<Record<Field, string>>) => {
    setFieldErrors(errors);
    setError([...new Set(Object.values(errors))].join(" "));
    return Object.keys(errors).length > 0;
  };
  useEffect(() => {
    if (step !== 3 || !registrationResult) return;
    if (countdown <= 0) {
      navigate("/login");
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, registrationResult, countdown, navigate]);

  const update = (field: Field, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      if (field === "password") delete next.confirmPassword;
      if (["npiNumber", "deaNumber", "stateLicense"].includes(field)) {
        delete next.npiNumber;
        delete next.deaNumber;
        delete next.stateLicense;
      }
      return next;
    });
    setError("");
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement> | React.FormEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (step === 1) {
      const errors: Partial<Record<Field, string>> = {};
      const requiredFields = {
        firstName: "First name", lastName: "Last name", email: "Email",
        companyName: "Company name", businessType: "Business type",
      } as const;
      for (const [field, label] of Object.entries(requiredFields)) {
        if (!form[field as keyof typeof requiredFields].trim()) {
          errors[field as Field] = `${label} is required.`;
        }
      }
      if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errors.email = "Enter a valid email address.";
      }
      if (form.password.length < 8) {
        errors.password = "Password must be at least 8 characters.";
      }
      if (!form.confirmPassword) {
        errors.confirmPassword = "Confirm password is required.";
      } else if (form.password !== form.confirmPassword) {
        errors.confirmPassword = "Confirm password must match your password.";
      }
      if (showValidationErrors(errors)) return;
    }
    setError("");
    if (step === 1) {
      const attribution = getLandingAttribution();
      const utm = getUtmParams();
      fetch("/api/auth/step1-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          companyName: form.companyName,
          email: form.email,
          phone: form.phone,
          password: form.password,
          businessType: form.businessType || undefined,
          ...attribution,
          visitorId: getVisitorId(),
          ...utm,
        }),
      }).then(res => {
        if (!res.ok) {
          res.json().then(data => {
            toast.error(data?.error || "Something went wrong saving your info. You can continue to the next step.");
          }).catch(() => {
            toast.error("Something went wrong saving your info. You can continue to the next step.");
          });
        }
      }).catch(() => {
        toast.error("Something went wrong. Please try again.");
      });
      trackLandingEvent("registration_start", attribution.landingPage || "/register", { step: 1 });
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleNext(e);
      return;
    }
    const errors: Partial<Record<Field, string>> = {};
    if (!form.npiNumber && !form.deaNumber && !form.stateLicense) {
      const message = "Provide an NPI, DEA, or state license number for verification (at least one is required).";
      errors.npiNumber = message;
      errors.deaNumber = message;
      errors.stateLicense = message;
    }
    if (form.npiNumber && !/^\d{10}$/.test(form.npiNumber)) {
      errors.npiNumber = "NPI must be exactly 10 digits.";
    }
    if (showValidationErrors(errors)) return;
    setLoading(true);
    setError("");
    const attribution = getLandingAttribution();
    const utm = getUtmParams();
    const result = await register({
      email: form.email,
      password: form.password,
      firstName: form.firstName,
      lastName: form.lastName,
      companyName: form.companyName,
      phone: form.phone,
      npiNumber: form.npiNumber,
      deaNumber: form.deaNumber,
      stateLicense: form.stateLicense,
      deaExpiration: form.deaExpiration,
      stateLicenseExpiration: form.stateLicenseExpiration,
      businessType: form.businessType || undefined,
      ...attribution,
      visitorId: getVisitorId(),
      ...utm,
    });
    setLoading(false);
    if (result.success) {
      setRegistrationResult(true);
      setStep(3);
      trackEvent("sign_up", { method: "email" });
      trackLandingEvent("registration_complete", attribution.landingPage || "/register", { method: "email" });
    } else {
      setError(result.error || "Registration failed");
    }
  };

  return (
    <>
      <SEO title="Create a Free Premier Pharma Account" description="Create a free Premier Pharma account with no credit card, contract, or purchase required. Licensed businesses are reviewed before activation." noindex />
      <section className="min-h-[80vh] flex items-center justify-center py-12 bg-background">
        <div className="card max-w-lg w-full mx-4">
          {step <= 2 && (
            <>
              <div className="text-center mb-6">
                <img src="/logo.svg" alt="Premier Pharma" className="h-10 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-primary">Create Your Account</h1>
                 <p className="text-gray-500 text-sm mt-1">Step {step} of 2 · Free to start</p>
                 <p className="text-gray-500 text-xs mt-2 max-w-sm mx-auto">
                   No credit card, contract, or purchase required. We review your licensing information before activation, typically within 1–2 business days.
                 </p>
                <div className="flex gap-2 mt-3 justify-center">
                  <div className={`h-1 w-16 rounded-full ${step >= 1 ? "bg-accent" : "bg-gray-200"}`}></div>
                  <div className={`h-1 w-16 rounded-full ${step >= 2 ? "bg-accent" : "bg-gray-200"}`}></div>
                </div>
              </div>

              <div role="alert" aria-atomic="true">
                {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>}
              </div>

              <form onSubmit={handleSubmit} noValidate>
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="reg-firstName" className="block text-sm font-medium text-primary mb-1">First Name *</label>
                        <input id="reg-firstName" type="text" required {...errorProps("firstName")} className="input-field" value={form.firstName} onChange={e => update("firstName", e.target.value)} />
                        {fieldError("firstName")}
                      </div>
                      <div>
                        <label htmlFor="reg-lastName" className="block text-sm font-medium text-primary mb-1">Last Name *</label>
                        <input id="reg-lastName" type="text" required {...errorProps("lastName")} className="input-field" value={form.lastName} onChange={e => update("lastName", e.target.value)} />
                        {fieldError("lastName")}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="reg-companyName" className="block text-sm font-medium text-primary mb-1">Company Name *</label>
                      <input id="reg-companyName" type="text" required {...errorProps("companyName")} className="input-field" value={form.companyName} onChange={e => update("companyName", e.target.value)} />
                      {fieldError("companyName")}
                    </div>

                    <div>
                      <label htmlFor="reg-businessType" className="block text-sm font-medium text-primary mb-1">Business Type *</label>
                      <select id="reg-businessType" required {...errorProps("businessType")} className="input-field" value={form.businessType} onChange={e => update("businessType", e.target.value)}>
                        <option value="">Select your business type…</option>
                        {BUSINESS_TYPES.map(type => <option key={type} value={type}>{BUSINESS_TYPE_LABELS[type]}</option>)}
                      </select>
                      {fieldError("businessType")}
                    </div>

                    <div>
                      <label htmlFor="reg-email" className="block text-sm font-medium text-primary mb-1">Email *</label>
                      <input id="reg-email" type="email" required {...errorProps("email")} className="input-field" value={form.email} onChange={e => update("email", e.target.value)} />
                      {fieldError("email")}
                    </div>

                    <div>
                      <label htmlFor="reg-phone" className="block text-sm font-medium text-primary mb-1">Phone</label>
                      <input id="reg-phone" type="tel" className="input-field" value={form.phone} onChange={e => update("phone", e.target.value)} />
                    </div>

                    <div>
                      <label htmlFor="reg-password" className="block text-sm font-medium text-primary mb-1">Password *</label>
                      <input id="reg-password" type="password" required {...errorProps("password")} className="input-field" value={form.password} onChange={e => update("password", e.target.value)} placeholder="Min 8 characters" />
                      {fieldError("password")}
                    </div>
                    <div>
                      <label htmlFor="reg-confirmPassword" className="block text-sm font-medium text-primary mb-1">Confirm Password *</label>
                      <input id="reg-confirmPassword" type="password" required {...errorProps("confirmPassword")} className="input-field" value={form.confirmPassword} onChange={e => update("confirmPassword", e.target.value)} />
                      {fieldError("confirmPassword")}
                    </div>

                    <button type="button" onClick={handleNext} className="btn-primary w-full">Next: License Info</button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <fieldset className="min-w-0 space-y-4" aria-describedby="reg-license-instructions">
                      <legend className="sr-only">License credentials</legend>
                     <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm mb-4">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>
                          <span className="font-semibold text-blue-800">License Verification Required</span>
                           <p id="reg-license-instructions" className="text-blue-700 mt-1">Provide at least one NPI, DEA, or state license number. Our team will review and verify it before activating your account. Shipping details can be added after approval.</p>
                        </div>
                      </div>
                    </div>

                    <div>
                       <label htmlFor="reg-npiNumber" className="block text-sm font-medium text-primary mb-1">NPI Number <span className="text-gray-500 font-normal">(if available)</span></label>
                      <input id="reg-npiNumber" type="text" {...errorProps("npiNumber")} className="input-field" value={form.npiNumber} onChange={e => update("npiNumber", e.target.value)} placeholder="10-digit NPI number" maxLength={10} />
                      {fieldError("npiNumber")}
                    </div>

                    <div>
                       <label htmlFor="reg-deaNumber" className="block text-sm font-medium text-primary mb-1">DEA Number <span className="text-gray-500 font-normal">(if applicable)</span></label>
                      <input id="reg-deaNumber" type="text" {...errorProps("deaNumber")} className="input-field" value={form.deaNumber} onChange={e => update("deaNumber", e.target.value)} placeholder="DEA registration number" maxLength={9} />
                      {fieldError("deaNumber")}
                    </div>

                    <div>
                      <label htmlFor="reg-deaExpiration" className="block text-sm font-medium text-primary mb-1">DEA Expiration Date</label>
                      <input id="reg-deaExpiration" type="date" className="input-field" value={form.deaExpiration} onChange={e => update("deaExpiration", e.target.value)} />
                    </div>

                    <div>
                       <label htmlFor="reg-stateLicense" className="block text-sm font-medium text-primary mb-1">State License Number <span className="text-gray-500 font-normal">(if applicable)</span></label>
                      <input id="reg-stateLicense" type="text" {...errorProps("stateLicense")} className="input-field" value={form.stateLicense} onChange={e => update("stateLicense", e.target.value)} placeholder="State pharmacy license" />
                      {fieldError("stateLicense")}
                    </div>

                    <div>
                      <label htmlFor="reg-stateLicenseExpiration" className="block text-sm font-medium text-primary mb-1">State License Expiration Date</label>
                      <input id="reg-stateLicenseExpiration" type="date" className="input-field" value={form.stateLicenseExpiration} onChange={e => update("stateLicenseExpiration", e.target.value)} />
                    </div>
                    </fieldset>

                    <div className="flex items-center gap-3 pt-2">
                      <div className="flex gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>NABP</span>
                        <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>DSCSA</span>
                        <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5"><span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>DEA</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button type="button" onClick={() => { setStep(1); setError(""); setFieldErrors({}); }} className="btn-outline flex-1">Back</button>
                      <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-50">
                        {loading ? "Creating..." : "Create Account"}
                      </button>
                    </div>
                  </div>
                )}
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Already have an account? <Link to="/login" className="text-accent font-medium hover:underline">Log in</Link>
              </p>
            </>
          )}

          {step === 3 && registrationResult && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h2 className="text-2xl font-bold text-primary mb-2">Thanks, {form.firstName}! Your request is in review.</h2>
              <p className="text-gray-600 mb-3">Your free account request is saved. Our team will review your licensing information before activation and email you when there is an update.</p>
              {getLandingAttribution().landingPage === "/lp/drug-sourcing" && (
                <p className="text-gray-600 mb-3">Next: we'll email you to collect your order list and book your free DSCSA readiness review.</p>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 mb-6">
                Verification typically takes 1–2 business days. There is no charge, contract, or purchase commitment. You can add shipping details after approval.
              </div>
              <p className="text-xs text-gray-500 mb-5">We use your information to review eligibility and operate your account. We do not sell your personal information.</p>
              <div className="mt-2">
                <Link to="/login" className="btn-primary inline-block px-8 py-3 text-lg">Go to Login</Link>
                <p className="text-sm text-gray-400 mt-3">Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}...</p>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
